import React, { useState, useEffect } from "react";
import { getTochlistadmin } from "../../services/websuerServices";
import { Icon } from "@iconify/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const WebUserListLayer = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(null); // keep Date object
  const [endDate, setEndDate] = useState(null); // keep Date object

  // ✅ initialize from URL
  const [typeFilter, setTypeFilter] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let typeFromUrl = searchParams.get("type");
    if (typeFromUrl && typeFromUrl !== "") {
      typeFromUrl =
        typeFromUrl.charAt(0).toUpperCase() +
        typeFromUrl.slice(1).toLowerCase();

      setTypeFilter(typeFromUrl);
    } else {
      setTypeFilter(""); // 👈 default when type is empty
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";
      //  Validation: check if start date is greater than end date
      if (
        formattedStartDate &&
        formattedEndDate &&
        moment(formattedStartDate).isAfter(moment(formattedEndDate))
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
        return; // stop fetch
      }

      const res = await getTochlistadmin({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder: sortOrder,
        status: statusFilter,
        type: typeFilter,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      console.log(res.data.data);
      setItems(res.data.data);
      setTotalItems(res.data.metadata.totalItem);
      setTotalPages(res.data.metadata.totalPages);
    } catch (error) {
      console.log(error);
      setErrorMessage("Failed to fetch booking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
    typeFilter,
    startDate,
    endDate,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getPaginationRange = () => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const maxVisiblePages = 5;
    const pages = [];
    
    if (totalPages <= maxVisiblePages) {
      // If total pages is 5 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate the start page for the visible range
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Adjust start page if we're near the end
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      {/* Header and Filters */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  setCurrentPage(1);
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          {/* Date Filters */}
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setEndDate(null); // reset endDate
                setCurrentPage(1);
              }}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              minDate={startDate} // Disable dates before the start date
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              minDate={startDate}
            />
          </div>
          
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    User Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "fullName" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "fullName" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Email
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "email" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "email" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                  <th
                  onClick={() => handleSort("contactnumber")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Contact number
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "contactnumber" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "contactnumbere" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
            
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Created Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
              
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((cl) => (
                  <tr key={cl._id}>
                    <td>{cl.fullName}</td>
                    <td>{cl.email}</td>
                    <td>{cl.contactnumber}</td>
                    <td>
                      {(() => {
                        const date = new Date(cl.createdAt);
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = date.getMonth() + 1;
                        const year = date.getFullYear();
                        return `${day}-${month}-${year}`;
                      })()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() => {
                  if (currentPage > 1) setCurrentPage((prev) => prev - 1);
                }}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>

            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}

            <li className="page-item">
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() => {
                  if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                }}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WebUserListLayer;
